import { useState, useRef, useEffect } from 'react'
import { Button, Checkbox, Spacing, Textbox } from '@/form/FormComponents'
import { FormProvider, FormSubmitProvider, FormDataProvider } from '@/form/FormProvider'
import { SignInContainer } from '@/pages/SignedOutLayout'
import { useErrorModal, useModal, Modal, ModalContent, ModalButtons } from '@/components/Modal';
import { Spinner } from '@/components/Spinner'
import { verifyEmailOobCode, verifyPasswordResetOobCode, confirmPasswordReset } from '@/systems/Authentication'
import { useParams, useNavigate, useSearchParams, useLoaderData } from 'react-router'
import { publicAction } from '@/data/loaders';
import { EmailPreferencesForm, EMAIL_PREFERENCE_CATEGORIES } from '@/pages/SettingsLayout';
import { Icon } from '@/components/Icon';

export function VerifyEmailLayout() {
  const navigate = useNavigate();
  const oobCode = useParams().oobCode;
  const [searchParams] = useSearchParams();
  const change = searchParams.has("change");
  const [errored, setErrored] = useState(false);
  const [success, setSuccess] = useState(false);
  const hasSendEmail = useRef(false);

  useEffect(function() {
    if (hasSendEmail.current) return;
    hasSendEmail.current = true;
    verifyEmailOobCode(oobCode).then(function() {
      if (change) {
        setSuccess(true);
      } else {
        window.location.href = "/login";
      }
    }).catch(function(error) {
      console.error(error);
      setErrored(true);
    });
  }, [oobCode, navigate, change]);

  if (success) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Email changed</div>
        <div className="setup_description">Your email has been changed. You can now sign in with your new email address.</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Continue" className="primary" onClick={() => window.location.href = "/login"}/>
        </div>
      </SignInContainer>
    )
  }

  if (errored) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Couldn't verify your email</div>
        <div className="setup_description">This link is either expired or invalid. Please try verifying your email again.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => navigate("/login", { replace: true })}/>
        </div>
      </SignInContainer>
    )
  }

  return (
    <SignInContainer loading={false}>
      <div className="setup_title">Verifying your email...</div>
      <Spinner style={{width: "50px", height: "50px", margin: "0 auto"}}/>
      <Spacing/>
      <Spacing/>
    </SignInContainer>
  )
}

export function ResetPasswordLayout() {
  const navigate = useNavigate();
  const oobCode = useParams().oobCode;
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [errored, setErrored] = useState(false);

  const hasVerifiedOobCode = useRef(false);

  useEffect(function() {
    if (hasVerifiedOobCode.current) return;
    hasVerifiedOobCode.current = true;
    verifyPasswordResetOobCode(oobCode).then(function() {
      setVerifying(false);
    }).catch(function(error) {
      console.error(error);
      setVerifying(false);
      setErrored(true);
    });
  }, [oobCode]);

  const handleSubmit = function({ formatted_data, focusError, updateData }) {
    if (loading) { return; }

    let password = formatted_data.password;
    let password_verify = formatted_data.password_verify;

    if (password !== password_verify) {
      focusError({name: "password_verify", select: true, error: "Passwords don't match"})
      return;
    }

    setLoading(true);

    confirmPasswordReset(oobCode, password).then(function() {
      setSuccess(true);
    }).catch(function(error) {
      setLoading(false);

      if (error.code == "auth/weak-password") {
        focusError({name: "password", error: "Please choose a stronger password"});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      } else if (error.code == "auth/invalid-action-code") {
        focusError({name: "password", error: "This link is either expired or invalid. Please try resetting your password again."});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      } else {
        console.error(error);
        focusError({name: "password", error: error.message});
        updateData((draft) => {
          draft.password = "";
          draft.password_verify = "";
        })
      }

    });
  }

  if (success) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Password updated</div>
        <div className="setup_description">You can now sign in with your new password.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => navigate("/login", { replace: true })}/>
        </div>
      </SignInContainer>
    )
  }

  if (errored) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Link expired</div>
        <div className="setup_description">This link is either expired or invalid. Please try resetting your password again.</div>
        <Spacing/>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Back to sign in" className="hollow" onClick={() => navigate("/login", { replace: true })}/>
        </div>
      </SignInContainer>
    )
  }

  if (verifying) {
    return (
      <SignInContainer loading={false}>
        <div className="setup_title">Choose a new password</div>
        <Spinner style={{width: "50px", height: "50px", margin: "0 auto"}}/>
        <Spacing/>
        <Spacing/>
      </SignInContainer>
    )
  }

  return (
    <SignInContainer loading={loading}>
      <FormProvider key="reset_password" onSubmit={handleSubmit} obviated={loading}>
        <FormSubmitProvider>
          {({handleSubmit}) => (
            <form onSubmit={(event) => event.preventDefault()} className="pmf-reset-password">
              <div className="setup_title">Choose a new password</div>
              <Textbox name="password" title="New password" required="Please choose a new password" placeholder="Password" onEnter={() => {}} autofocus={true} type="password" autoComplete="new-password"/>
              <Spacing/>
              <Textbox name="password_verify" title="Re-type new password" required="Please re-type your new password" placeholder="Password" onEnter={handleSubmit} type="password" autoComplete="new-password"/>
              <Spacing/>
              <Spacing/>
              <div className="buttons right">
                <Button text="Change password" className="primary" submit onClick={handleSubmit}/>
              </div>
            </form>
          )}
        </FormSubmitProvider>
      </FormProvider>
    </SignInContainer>
  )
}

export function EmailPreferencesLinkLayout() {
  const { token, value } = useLoaderData();

  return (
    value ? (
      value.kind === "non_user" ? (
        <NonUserEmailPreferences token={token} value={value} />
      ) : (
        <UserEmailPreferences token={token} value={value} />
      )
    ) : (
      <SignInContainer loading={false}>
        <div className="setup_title">Link expired</div>
        <div className="setup_description">{`Sorry, this link is expired, so you weren't unsubscribed. Please click the unsubscribe link in a more recent email.`}</div>
        <Spacing/>
        <Spacing/>
      </SignInContainer>
    )
  )
}

function NonUserEmailPreferences({ token, value }) {
  const [loading, setLoading] = useState(false);
  const [openErrorModal] = useErrorModal();
  const [screen, setScreen] = useState(value?.unsubscribed ? "resubscribe_prompt" : "unsubscribe_prompt");

  const [openSubmittedModal] = useModal(({ context, closeModal }) => {
    return (
      <Modal width={330}>
        <ModalContent>
          <div className="assignment_submitted">
            <Icon name="check-circle"/>
            <div>{context.unsubscribed ? "You're unsubscribed" : "You've resubscribed"}</div>
            <div>{context.unsubscribed ? "You won't get any more emails." : "You'll start getting emails again."}</div>
          </div>
        </ModalContent>
        <ModalButtons centered={true}>
          <Button text="Close" onClick={closeModal} /> 
        </ModalButtons>
      </Modal>
    )
  });

  function handleUnsubscribeAll() {
    setLoading(true);
    publicAction("updateEmailPreferencesByToken", { token, unsubscribed: true }, { unauthenticated: true }).then(function () {
      setLoading(false);
      setScreen("unsubscribe_success");
      openSubmittedModal({ unsubscribed: true });
    }).catch(function (error) {
      console.error(error);
      setLoading(false);
      openErrorModal();
    });
  }

  function handleResubscribe() {
    setLoading(true);
    publicAction("updateEmailPreferencesByToken", { token, unsubscribed: false }, { unauthenticated: true }).then(function () {
      setLoading(false);
      setScreen("unsubscribe_prompt");
      openSubmittedModal({ unsubscribed: false });
    }).catch(function (error) {
      console.error(error);
      setLoading(false);
      openErrorModal();
    });
  }

  if (screen == "unsubscribe_prompt" || screen == "unsubscribe_success") {
    return (
      <SignInContainer>
        <div className="setup_title">{`Unsubscribe from all emails?`}</div>
        <div className="setup_description">{`If you unsubscribe, you will no longer receive invitations or shared files from Sapient Clinician users.`}</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          {screen == "unsubscribe_prompt" ? (
            <Button text="Unsubscribe from all" className="primary" disabled={loading} loading={loading} onClick={handleUnsubscribeAll}/>
          ) : (
            <Button text="Resubscribe" disabled={loading} loading={loading} onClick={handleResubscribe}/>
          )}
        </div>
      </SignInContainer>
    );
  } else if (screen == "resubscribe_prompt") {
    return (
      <SignInContainer>
        <div className="setup_title">{`You're unsubscribed`}</div>
        <div className="setup_description">{`You won't receive any invitations or shared files from Sapient Clinician users.`}</div>
        <Spacing/>
        <Spacing/>
        <div className="buttons center">
          <Button text="Resubscribe" className="primary" disabled={loading} loading={loading} onClick={handleResubscribe}/>
        </div>
      </SignInContainer>
    );
  }
}

function UserEmailPreferences({ token, value }) {
  const [loading, setLoading] = useState(false);
  const [openErrorModal] = useErrorModal();

  const initial_initial_data = {
    ...EMAIL_PREFERENCE_CATEGORIES.reduce((acc, category) => {
      acc[category] = value.email_preferences?.[category] === false ? false : true;
      return acc;
    }, {}),
  };

  const [initial_data, setInitialData] = useState(initial_initial_data);

  const [openSubmittedModal] = useModal(({ closeModal }) => {
    return (
      <Modal width={330}>
        <ModalContent>
          <div className="assignment_submitted">
            <Icon name="check-circle"/>
            <div>Email preferences saved</div>
          </div>
        </ModalContent>
        <ModalButtons centered={true}>
          <Button text="Close" onClick={closeModal} /> 
        </ModalButtons>
      </Modal>
    )
  });

  function handleSavePreferences({ formatted_data }) {
    setLoading(true);
    publicAction("updateEmailPreferencesByToken", { token, email_preferences: formatted_data }, { unauthenticated: true }).then(function () {
      setLoading(false);
      setInitialData(formatted_data);
      openSubmittedModal();
    }).catch(function (error) {
      console.error(error);
      setLoading(false);
      openErrorModal();
    });
  }

  return (
    <SignInContainer>
      <FormProvider key="user_email_preferences" initial_data={initial_data} onSubmit={handleSavePreferences}>
        <FormSubmitProvider>
          {({ handleSubmit, has_changed }) => {
            return (
              <>
                <div className="setup_title">Email preferences</div>
                <EmailPreferencesForm/>
                <Spacing/>
                <Spacing/>
                <div className="buttons sides">
                  <FormDataProvider>
                    {({ updateData }) => {
                      return (
                        <Button text="Unsubscribe from all" className="hollow" disabled={loading} onClick={() => {
                          updateData(function (draft) {
                            EMAIL_PREFERENCE_CATEGORIES.forEach(function (key) {
                              draft[key] = false;
                            });
                          });
                          setTimeout(() => handleSubmit(), 0);
                        }}/>
                      )
                    }}
                  </FormDataProvider>
                  <Button text="Save changes" className="primary" loading={loading} disabled={loading || !has_changed} onClick={handleSubmit}/>
                </div>
              </>
            );
          }}
        </FormSubmitProvider>
      </FormProvider>
    </SignInContainer>
  );
}